<template>
  <div 
    v-show="!isLoading"
    class="document-reader-container"
  >
    <!-- Document Viewer Section -->
    <div 
      class="viewer-section"
      data-test="viewer-section"
    >
      <!-- Non-scanned PDF Viewer -->
      <PDFViewer
        v-if="isNonScannedPdf && pdfFile"
        :pdf-file="pdfFile"
        :active-sentence-text="vrActiveRawSentenceText"
        @document-loaded="onDocumentLoaded"
        @text-selected="onTextSelected"
        @page-changed="onPageChanged"
      />

      <!-- Scanned PDF Viewer with OCR Overlay -->
      <ScannedPDFViewer
        v-else-if="isScannedPdf && pdfFile"
        :pdf-file="pdfFile"
        :parsed-lines="parsedLines"
        :page-grouped-lines="pageGroupedLines"
        :available-pages="availablePages"
        :disable-hover-effects="disableHoverEffects"
        :active-polygon-indices="vrActivePolygonIndices"
        @document-loaded="onDocumentLoaded"
        @polygon-clicked="onPolygonClicked"
        @page-changed="onPageChanged"
      />

      <!-- Image Viewer with OCR Overlay -->
      <ImageViewer
        v-else-if="isImage && imgFile"
        :img-file="imgFile"
        :parsed-lines="parsedLines"
        :page-grouped-lines="pageGroupedLines"
        :disable-hover-effects="disableHoverEffects"
        :active-polygon-indices="vrActivePolygonIndices"
        @image-loaded="onDocumentLoaded"
        @polygon-clicked="onPolygonClicked"
      />

      <!-- Text Document Viewer -->
      <TextViewer
        v-if="isText"
        :document-sections="documentSections"
        :active-sentence-text="vrActiveSentenceText"
        @document-loaded="onDocumentLoaded"
        @text-selected="onTextSelected"
        @text-selected-on-text-reader="textSelectedOnTextReader"
      />

      <!-- Word Document Viewer -->
      <WordViewer
        v-else-if="isWord && docxFile"
        :docx-file="docxFile"
        :active-sentence-text="vrActiveSentenceText"
        @document-loaded="onDocumentLoaded"
        @text-selected="onTextSelected"
        @text-selected-on-text-reader="textSelectedOnTextReader"
      />

      <!-- Web Content Viewer -->
      <WebViewer
        v-else-if="isUrl"
        :html-content="htmlContent"
        :source-url="resource.resource_url"
        :active-sentence-text="vrActiveSentenceText"
        @document-loaded="onDocumentLoaded"
        @text-selected="onTextSelected"
        @hide-url-reader="$emit('hide-reader-content')"
      />

      <!-- Loading/Error States -->
      <div
        v-if="resourceLoading"
        class="loading-state"
      >
        <p>Loading resource...</p>
      </div>
      <div
        v-if="resourceError"
        class="error-state"
      >
        <p>Error loading resource: {{ resourceError }}</p>
      </div>
    </div>
  </div>
</template><script>
import PDFViewer from './viewers/PDFViewer.vue';
import ScannedPDFViewer from './viewers/ScannedPDFViewer.vue';
import ImageViewer from './viewers/ImageViewer.vue';
import TextViewer from './viewers/TextViewer.vue';
import WordViewer from './viewers/WordViewer.vue';
import WebViewer from './viewers/WebViewer.vue';
import { useSocketCommunication } from './composables/useSocketCommunication';
import { useParsedResults } from './composables/useParsedResults';
import vocabularyService from '@/services/vocabularyService';
import { RESOURCE_TYPE_PDF, RESOURCE_TYPE_IMG, RESOURCE_TYPE_DOCX, RESOURCE_TYPE_TXT, RESOURCE_TYPE_URL, REQUEST_TYPE_SENTENCE_ANALYSIS } from '@/assets/constants/constants';
import { BASE_URL, DOWNLOAD_PDF_API_ENDPOINT, DOWNLOAD_IMG_API_ENDPOINT, DOWNLOAD_DOCX_API_ENDPOINT, WORD_EXTRACTION_ENDPOINT } from '@/assets/constants/api_endpoints';
import { WS_MESSAGE_DOCUMENT_CONTENT, WS_MESSAGE_ERROR, WS_MESSAGE_VOCABULARY_DEFINITION } from '@/assets/constants/constants';
import { createAuthenticatedAxiosInstance } from '@/services/api';
import { getWithAuthentication } from "@/services/api";
import DOMPurify from 'dompurify';

/**
 * DocumentReader - Main orchestrator component
 * Routes documents (PDFs and images) to appropriate viewer components
 * Manages WebSocket communication for text analysis
 * Handles OCR data loading for scanned PDFs and images
 */


export default {
    name: 'DocumentReader',
    components: {
        PDFViewer,
        ScannedPDFViewer,
        ImageViewer,
        TextViewer,
        WordViewer,
        WebViewer,
    },
    props: {
        isLoading: {
            type: Boolean,
            default: true
        },
        disableHoverEffects: {
            type: Boolean,
            default: false
        },
        vrActiveSentenceText: {
            type: String,
            default: null
        },
        vrActiveRawSentenceText: {
            type: [String, Array],
            default: null
        },
        vrActivePolygonIndices: {
            type: Array,
            default: null
        },
    },
    emits: ['resourceLoaded', 'analysisComplete', 'updateLoadingStatus', 'updateVocabularyList', 'startVocabularyCheck', 'page-changed', 'open-summary', 'hide-reader-content'],
    setup() {
        const {
            socket,
            initializeSocket,
            setupSocketHandlers,
            sendAnalysisRequest,
            sendLoadDocumentRequest,
            closeSocket
        } = useSocketCommunication();

        const {
            parsedLoading,
            parsedError,
            parsedLines,
            pageGroupedLines,
            availablePages,
            loadParsedResults
        } = useParsedResults();

        return {
            socket,
            initializeSocket,
            setupSocketHandlers,
            sendAnalysisRequest,
            sendLoadDocumentRequest,
            closeSocket,
            parsedLoading,
            parsedError,
            parsedLines,
            pageGroupedLines,
            availablePages,
            loadParsedResults,
        };
    },
    data() {
        return {
            pdfFile: null,
            imgFile: null,
            docxFile: null,
            htmlContent: null,
            resourceLoading: false,
            resourceError: null,
            selectedText: null,
            selectedPolygon: null,
            currentPage: 1,
            documentSections: [],
            currentVocabularyLookup: null,
        };
    },

    computed: {
        resource() {
            return this.$store.state.readerDocument || {};
        },
        isNonScannedPdf() {
            return this.resource.resource_type === RESOURCE_TYPE_PDF && !this.isScannedPdf;
        },
        isScannedPdf() {
            return this.resource.resource_type === RESOURCE_TYPE_PDF && !!this.resource.is_scanned;
        },
        isImage() {
            return this.resource.resource_type === RESOURCE_TYPE_IMG;
        },
        isWord() {
            return this.resource.resource_type === RESOURCE_TYPE_DOCX;
        },
        isText() {
            return this.resource.resource_type === RESOURCE_TYPE_TXT;
        },
        isUrl() {
            return this.resource.resource_type === RESOURCE_TYPE_URL;
        },
        isMobile() {
            return window.innerWidth <= 599;
        }
    },

    watch: {
        resource: {
            handler(newResource) {
                if (newResource && newResource.id) {
                    this.loadResource();
                }
            },
            immediate: true,
            deep: true
        }
    },

    created() {
        // Initialize socket connections
        const authToken = localStorage.getItem('authToken');
        this.initializeSocket(authToken);
    },

    mounted() {
        // Setup socket event handlers
        this.setupWebSocketHandlers();
        // Listen for window resize to update mobile state
        window.addEventListener('resize', this.handleWindowResize);
        // Demo simulation: allow the simulation service to trigger a polygon click
        // without needing direct access to the WebViewer iframe internals.
        window.addEventListener('demo:simulate-polygon-click', this.onDemoSimulatePolygonClick);
    },

    beforeUnmount() {
        // Cancel any pending vocabulary lookup
        vocabularyService.cancelLookup();
        // Close socket connections
        this.closeSocket();
        // Remove resize listener
        window.removeEventListener('resize', this.handleWindowResize);
        window.removeEventListener('demo:simulate-polygon-click', this.onDemoSimulatePolygonClick);
    },

    methods: {
        handleWindowResize() {
            this.$forceUpdate();
        },

        /**
         * Demo simulation handler — triggers a vocabulary lookup using the first
         * available polygon from the parsed OCR data, without touching the iframe.
         */
        onDemoSimulatePolygonClick() {
            const pages = Object.keys(this.pageGroupedLines);
            if (!pages.length) return;
            const firstPage = pages[0];
            const polygons = this.pageGroupedLines[firstPage];
            if (!polygons || !polygons.length) return;
            this.onPolygonClicked({ polygon: polygons[2], page: parseInt(firstPage, 10) });
        },


        async loadResource() {
            if (!this.resource || !this.resource.id) {
                return;
            }

            this.resourceLoading = true;
            this.resourceError = null;
            this.htmlContent = null;
            this.documentSections = []; // Reset sections for new document

            try {

                // Download the actual file (PDF, image, or Word)
                // Text documents are streamed via WebSocket, no download needed
                if (this.resource.resource_type === RESOURCE_TYPE_PDF) {
                    await this.downloadPdfFile();
                } else if (this.resource.resource_type === RESOURCE_TYPE_IMG) {
                    await this.downloadImgFile();
                } else if (this.resource.resource_type === RESOURCE_TYPE_DOCX) {
                    await this.downloadDocxFile();
                } else if (this.resource.resource_type === RESOURCE_TYPE_URL) {
                    // For URLs, content will be fetched and streamed via WebSocket
                    if (!this.resource.resource_url) {
                        throw new Error('URL not provided');
                    }
                }

                // For scanned PDFs, load OCR polygon data
                if (this.resource.resource_type === RESOURCE_TYPE_PDF && this.resource.is_scanned) {
                    try {
                        await this.loadParsedResults(
                            this.resource.id,
                            this.resource.is_sample,
                            (error) => {
                                this.parsedError = error;
                            }
                        );
                    } catch (error) {
                        // Failed to load OCR data
                    }
                }

                // For images, load OCR data
                if (this.resource.resource_type === RESOURCE_TYPE_IMG) {
                    try {
                        await this.loadParsedResults(
                            this.resource.id,
                            this.resource.is_sample,
                            (error) => {
                                this.resourceError = error;
                            }
                        );
                    } catch (error) {
                        // Failed to load OCR data for image
                    }
                }

                // Send document info to WebSocket
                await this.waitForSocketReady();
                this.sendLoadDocumentRequest(this.resource);
                this.$emit('resourceLoaded', this.resource);
                this.$emit('updateLoadingStatus', false);
            } catch (error) {
                this.resourceError = error.message || 'Failed to load resource';
                this.$emit('updateLoadingStatus', false);
            } finally {
                this.resourceLoading = false;
            }
        },

        openSummaryTab() {
            this.$emit('open-summary');
        },
        // Handle text selection from child viewers (TextViewer, WordViewer)
        async textSelectedOnTextReader(e) {
            const selection = window.getSelection();
            const selectedText = selection.toString().trim();

            if (selectedText.length > 0) {
                this.onTextSelected({text: selectedText, pageNumber: 1 });
                setTimeout(() => selection.removeAllRanges(), 800);
                return;
            }

            const tapTouch = e.changedTouches[0];
            if (!tapTouch) return;

            const caretRange = document.caretRangeFromPoint
                ? document.caretRangeFromPoint(tapTouch.clientX, tapTouch.clientY)
                : null;

            if (!caretRange || caretRange.startContainer.nodeType !== Node.TEXT_NODE) return;

            const textNode = caretRange.startContainer;
            const offset = caretRange.startOffset;
            const text = textNode.textContent;

            const start = Math.max(0, offset - 2);
            const end = Math.min(text.length, offset + 3);

            let potentialWord = text.slice(start, end).trim();

            const url = `${WORD_EXTRACTION_ENDPOINT}?text=${potentialWord}&char=${text[offset]}`;

            const response = await getWithAuthentication(BASE_URL, url);
            if (response && response.status === 200) {
            const data = JSON.parse(response.data);
            if (!data['word']) return;

            // Briefly highlight the extracted word in the text node
            const wordIndex = text.indexOf(data['word'], Math.max(0, offset - data['word'].length));
            if (wordIndex !== -1) {
                const wordRange = document.createRange();
                wordRange.setStart(textNode, wordIndex);
                wordRange.setEnd(textNode, wordIndex + data['word'].length);
                selection.removeAllRanges();
                selection.addRange(wordRange);
            }

            this.onTextSelected({text: data['word'], pageNumber: 1 });
            setTimeout(() => selection.removeAllRanges(), 800);
            }            
        },

        async downloadPdfFile() {
            try {
                const axiosInstance = createAuthenticatedAxiosInstance();
                const downloadUrl = `${BASE_URL}${DOWNLOAD_PDF_API_ENDPOINT}?resource_id=${this.resource.id}&is_sample=${this.resource.is_sample}`;
                const response = await axiosInstance.get(downloadUrl, { responseType: 'blob' });
                this.pdfFile = response.data;
            } catch (error) {
                throw error;
            }
        },

        async downloadImgFile() {
            try {
                const axiosInstance = createAuthenticatedAxiosInstance();
                const downloadUrl = `${BASE_URL}${DOWNLOAD_IMG_API_ENDPOINT}?resource_id=${this.resource.id}&is_sample=${this.resource.is_sample}`;
                const response = await axiosInstance.get(downloadUrl, { responseType: 'blob' });
                this.imgFile = response.data;
            } catch (error) {
                throw error;
            }
        },

        async downloadDocxFile() {
            try {
                const axiosInstance = createAuthenticatedAxiosInstance();
                const downloadUrl = `${BASE_URL}${DOWNLOAD_DOCX_API_ENDPOINT}?resource_id=${this.resource.id}&is_sample=${this.resource.is_sample}`;
                const response = await axiosInstance.get(downloadUrl, { responseType: 'blob' });
                this.docxFile = response.data;
            } catch (error) {
                throw error;
            }
        },

        onDocumentLoaded() {

        },

        onPageChanged(pageNumber) {
            this.currentPage = pageNumber;
            this.$emit('page-changed', pageNumber);
        },

        onTextSelected(data) {
            this.selectedText = data.text;
            this.selectedPolygon = null;
            this.currentPage = data.pageNumber;
            this.showSentenceAnalysis(data.text, data.pageNumber);
        },

        onPolygonClicked(data) {
            this.selectedPolygon = data.polygon;
            this.selectedText = data.polygon.text;
            this.currentPage = data.page;
            this.showSentenceAnalysis(data.polygon.text, data.page);

            // Notify the demo overlay: polygon clicks happen inside the WebViewer iframe
            // and don't bubble out to the viewer-section element in the main document.
            // Dispatching a synthetic click here bridges that boundary so the demo can
            // advance to the next step when the user clicks a real polygon.
            const viewerSection = document.querySelector('[data-test="viewer-section"]');
            if (viewerSection) {
                viewerSection.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
            }
        },

        showSentenceAnalysis(text, pageNumber) {
            if (!text || text.trim().length === 0) {
                return;
            }

            this.$emit('startVocabularyCheck');
            
            // Execute vocabulary lookup with timeout handling
            vocabularyService.executeVocabularyLookup(
                () => this.sendAnalysisRequest(text),
                (vocabulary) => {
                    // Vocabulary received - emission handled by handleWebSocketMessage
                }
            ).catch((error) => {
                // Handle timeout or error
                console.warn('Vocabulary lookup error:', error.message);
                // Emit an empty vocabulary to stop the spinner
                this.$emit('updateVocabularyList', null);
            });
        },

        setupWebSocketHandlers() {
            this.setupSocketHandlers(
                (message) => this.handleWebSocketMessage(message),
                (error) => this.handleWebSocketError(error),
                () => this.handleWebSocketClose()
            );
        },

        handleWebSocketMessage(message) {
            if (message.type === WS_MESSAGE_VOCABULARY_DEFINITION) {
                // Notify vocabulary service of successful receipt
                vocabularyService.notifyVocabularyReceived(message.vocabulary);
                // Also emit for compatibility with existing listeners
                this.$emit('updateVocabularyList', message.vocabulary);
            } else if (message.type === WS_MESSAGE_DOCUMENT_CONTENT) {
                // Handle URL HTML content streamed via WebSocket
                if (message.section) {
                    // For URL resources, section contains HTML content
                    if (this.isUrl) {
                        this.htmlContent = DOMPurify.sanitize(message.section);
                    } else {
                        // For text resources, append text section
                        this.documentSections.push(message.section);
                    }
                }
                this.$emit('updateLoadingStatus', false);
            } else if (message.type === WS_MESSAGE_ERROR) {
                this.$emit('updateLoadingStatus', false);
            }
            
            this.$emit('analysisComplete', message);
        },

        handleWebSocketError(error) {
            console.error('WebSocket error:', error);
        },

        handleWebSocketClose() {
            console.log('WebSocket connection closed');
        },

        waitForSocketReady() {
            return new Promise((resolve) => {
                if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                    resolve();
                    return;
                }

                const maxAttempts = 50;
                let attempts = 0;

                const checkSocket = setInterval(() => {
                    attempts++;
                    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                        clearInterval(checkSocket);
                        resolve();
                    } else if (attempts >= maxAttempts) {
                        clearInterval(checkSocket);
                        console.warn('WebSocket did not connect in time, proceeding anyway');
                        resolve();
                    }
                }, 100);
            });
        }
    }
};
</script>

<style scoped>
.document-reader-container {
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    background-color: var(--color-bg);
}

.viewer-section {
    position: relative;
    display: flex;
    flex-direction: column;
    text-align: left;
    flex: 1;
    background-color: var(--color-bg-alt);
    border: 1px solid var(--color-primary-mid);
    border-radius: 10px;
    min-height: 0;
    overflow: auto;
}

/* Loading and Error States */
.loading-state {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 100;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transition: all 0.3s ease;
}

/* Loading and Error States */
.loading-state,
.error-state {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #666;
}

.error-state {
    color: #d32f2f;
}

@media (max-width: 768px) {
    .loading-state {
        display: none;
    }

    .document-reader-container {
        flex-direction: column;
    }

    .viewer-section {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid rgb(70, 30, 15);
        height: 100%;
        padding: 0;
    }
}
</style>
